import { useCallback, useRef } from "react";

const RATIO_16_9 = 16 / 9;

// Consumed by styles inside `@supports not (aspect-ratio: 1 / 1)`
export const RATIO_BOX_WIDTH_VAR = "--ratio-box-width";
export const RATIO_BOX_HEIGHT_VAR = "--ratio-box-height";

/**
 * Which container dimension drives the ratio box:
 * - `height`: box height = container height, width derived (fit to available height)
 * - `width`: box width = container width, height derived (the padding-top hack direction)
 * - `contain`: largest box of `ratio` that fits inside both container dimensions
 */
export type RatioBoxMode = "height" | "width" | "contain";

type AspectRatioFallbackOptions = {
  ratio?: number;
  mode?: RatioBoxMode;
};

type RatioBox = { width: number; height: number };

const supportsAspectRatio = () =>
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("aspect-ratio", "1 / 1");

const measureBox = (
  mode: RatioBoxMode,
  ratio: number,
  containerWidth: number,
  containerHeight: number,
): RatioBox | null => {
  switch (mode) {
    case "width": {
      if (!containerWidth) return null;
      return {
        width: containerWidth,
        height: Math.round(containerWidth / ratio),
      };
    }
    case "contain": {
      if (!containerWidth || !containerHeight) return null;
      const width = Math.min(
        containerWidth,
        Math.round(containerHeight * ratio),
      );
      return { width, height: Math.round(width / ratio) };
    }
    case "height":
    default: {
      if (!containerHeight) return null;
      return {
        width: Math.min(Math.round(containerHeight * ratio), containerWidth),
        height: containerHeight,
      };
    }
  }
};

/**
 * Chromium <= 87 (webOS 22) has no `aspect-ratio`, and percentage lengths do not
 * resolve against a flex-sized ancestor, so a ratio box collapses to zero.
 * Measure the container there and publish the box size as CSS variables.
 */
export function useAspectRatioFallback<T extends HTMLElement = HTMLDivElement>({
  ratio = RATIO_16_9,
  mode = "height",
}: AspectRatioFallbackOptions = {}) {
  const cleanupRef = useRef<(() => void) | null>(null);

  return useCallback(
    (container: T | null) => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      if (!container || supportsAspectRatio()) return;

      const measure = () => {
        const box = measureBox(
          mode,
          ratio,
          container.clientWidth,
          container.clientHeight,
        );
        if (!box) return;
        container.style.setProperty(RATIO_BOX_WIDTH_VAR, `${box.width}px`);
        container.style.setProperty(RATIO_BOX_HEIGHT_VAR, `${box.height}px`);
      };

      // The container is sized by its ancestors, not by the box we publish, so
      // observing it cannot feed back into its own size. It also covers the
      // cases a window `resize` misses: the dialog opening (container goes from
      // `display: none` to laid out) and sibling content growing.
      if (typeof ResizeObserver !== "undefined") {
        const observer = new ResizeObserver(measure);
        observer.observe(container);
        cleanupRef.current = () => observer.disconnect();
        return;
      }

      const frame = requestAnimationFrame(measure);
      window.addEventListener("resize", measure);
      cleanupRef.current = () => {
        cancelAnimationFrame(frame);
        window.removeEventListener("resize", measure);
      };
    },
    [ratio, mode],
  );
}
